import {useSignal} from '@preact/signals-react'
import {useSignals} from '@preact/signals-react/runtime'
import React, {forwardRef, useImperativeHandle, useRef} from 'react'
import {useAuth} from '../../authentication/AuthProvider'
import {component} from '../../form/Constant'
import {isArrayAndNotEmpty} from '../../observations/Utils'
import {useManagementRoleList} from '../../role/query-keys/RoleKeysManager'
import StyledDropdown from '../components/StyledDropdown'

const StyledRoleOptions = forwardRef(
    (
        {
            value = null,
            onChange = () => {},
            optionType = component.multiSelect,
            placeholder = 'Select Hospital',
        },
        ref,
    ) => {
        useSignals()

        useImperativeHandle(ref, () => ({
            getValue: () => handleGetValue(),
            changeValue: newValue => handleChangeValue(newValue),
        }))

        //#region Context
        const {user} = useAuth()
        //#endregion

        //#region Value
        const role = useSignal(value)
        const optionRef = useRef(null)

        const handleGetValue = () => {
            return optionRef.current?.getValue()
        }

        const handleChangeValue = newValue => {
            optionRef.current?.changeValue(newValue)
        }

        const onRoleChange = selectedRoleId => {
            const selectedRole = options.find(
                option => option.value === selectedRoleId,
            )
            role.value = selectedRole

            if (onChange) {
                onChange(selectedRole)
            }
        }

        const checkIfQualifiedForTakingCarePatients = selectedRole => {
            if (
                !selectedRole ||
                !selectedRole.extraData ||
                !isArrayAndNotEmpty(selectedRole.extraData)
            ) {
                return null
            }

            const permissions = selectedRole.extraData

            const canTakeCarePatients = permissions.find(
                permission => permission?.qualified,
            )

            if (!!canTakeCarePatients) {
                return (
                    <div>
                        <span className="mr-2">
                            <i className="pi pi-check-circle text-darkcyan"></i>
                        </span>
                        This role ({role.value.label}) is{' '}
                        <span className="font-bold text-darkcyan">
                            qualified{' '}
                        </span>
                        for taking care of patients
                    </div>
                )
            }

            return (
                <div>
                    <span className="mr-2">
                        <i
                            className="pi pi-exclamation-triangle"
                            style={{color: 'red'}}></i>
                    </span>
                    This role ({role.value.label}) is{' '}
                    <span className="font-bold" style={{color: 'red'}}>
                        not qualified{' '}
                    </span>
                    for taking care of patients
                </div>
            )
        }
        //#endregion

        //#region Dropdown
        const {data: options} = useManagementRoleList(
            user?.id,
            true,
            {},
            !!user?.id,
        )

        const valueTemplate = (option, props) => {
            if (option) {
                return (
                    <div className="flex align-items-center">
                        <i
                            className="pi pi-user-plus mr-2"
                            style={{fontSize: '16px'}}></i>

                        <div className="font-bold">
                            {option.label}{' '}
                            {option?.data ? ` - ${option.data}` : ''}
                        </div>
                    </div>
                )
            }

            return <span>{props.placeholder}</span>
        }

        const itemTemplate = option => {
            return (
                <div className="flex align-items-center">
                    <div
                        className="flex align-items-center justify-content-center mr-2"
                        style={{width: '48px'}}>
                        <div>
                            <i
                                className="pi pi-user-plus"
                                style={{fontSize: '20px'}}></i>
                        </div>
                    </div>
                    <div>
                        <h6 className="m-0 mb-1 font-bold">{option.label}</h6>
                        <small>{option?.data}</small>
                    </div>
                </div>
            )
        }
        //#endregion

        return (
            <React.Fragment>
                <div className="mb-2">
                    <StyledDropdown
                        ref={optionRef}
                        options={options || []}
                        value={value}
                        onChange={onRoleChange}
                        placeholder={placeholder}
                        itemTemplate={itemTemplate}
                        valueTemplate={valueTemplate}
                        showClear={true}
                        hasPanelFooter={true}
                    />
                </div>
                {!role.value ? (
                    <p className="py-2">
                        <i className="pi pi-info-circle mr-2"></i>
                        <span>
                            Please select a role to check if it is qualified for
                            taking care of patients
                        </span>
                    </p>
                ) : (
                    checkIfQualifiedForTakingCarePatients(role.value)
                )}
            </React.Fragment>
        )
    },
)

export default StyledRoleOptions
